"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const tiers = [{
  id: 1,
  name: "The Mix",
  price: "450",
  description: "Professional sonic balancing and clarity for your individual tracks.",
  isPopular: false,
  buttonText: "Book Session",
  buttonVariant: "outline" as const,
  link: "/ServicesPage"
}, {
  id: 2,
  name: "The Master",
  price: "150",
  description: "The final polish. Industry-standard loudness and tonal consistency.",
  isPopular: true,
  buttonText: "Get Mastered",
  buttonVariant: "default" as const,
  link: "/ServicesPage"
}, {
  id: 3,
  name: "The Full Production",
  price: "2,500",
  description: "From concept to final master. Complete creative direction and engineering.",
  isPopular: false,
  buttonText: "Start Project",
  buttonVariant: "outline" as const,
  link: "/ServicesPage"
}];
const features = [{
  tierId: 1,
  text: "Vocal Tuning & Alignment"
}, {
  tierId: 1,
  text: "Analog Summing"
}, {
  tierId: 1,
  text: "3 Revision Rounds"
}, {
  tierId: 1,
  text: "WAV & MP3 Deliverables"
}, {
  tierId: 2,
  text: "Stereo Enhancement"
}, {
  tierId: 2,
  text: "Dynamic EQ & Compression"
}, {
  tierId: 2,
  text: "Streaming Platform Optimization"
}, {
  tierId: 2,
  text: "DDP Image for CD"
}, {
  tierId: 2,
  text: "24-hour Turnaround"
}, {
  tierId: 3,
  text: "Custom Beat Composition"
}, {
  tierId: 3,
  text: "Full Studio Access"
}, {
  tierId: 3,
  text: "Unlimited Revisions"
}, {
  tierId: 3,
  text: "Session Musician Booking"
}, {
  tierId: 3,
  text: "Distribution Strategy"
}];
export default function ServicePackages() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="264668" id="service-packages" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[500px] w-[500px] rounded-md bg-primary/5 opacity-40 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] h-[600px] w-[600px] rounded-md bg-primary/5 opacity-20 blur-[150px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerReveal.ref} className={cn("mx-auto max-w-3xl text-center transition-all duration-1000 ease-out", headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <Badge variant="outline" className="mb-4 border-border text-muted-foreground font-mono uppercase tracking-widest text-[10px]">
            <Sparkles className="mr-1 h-3 w-3" />
            Studio Rates
          </Badge>
          <h2 className="font-default text-5xl font-bold tracking-tighter sm:text-7xl uppercase">
            Sonic Excellence
          </h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground font-sans max-w-2xl mx-auto">
            Precision engineering for the modern artist. Choose the production tier that resonates with your creative vision.
          </p>
        </div>

        <div ref={gridReveal.ref} className={cn("mx-auto mt-16 grid max-w-md grid-cols-1 gap-8 lg:max-w-6xl lg:grid-cols-3 lg:gap-8 transition-all duration-1000 delay-200 ease-out", gridReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
          {tiers.map((tier, index) => {
          const tierFeatures = features.filter(f => f.tierId === tier.id);
          const isHighlighted = tier.isPopular;
          return <div key={tier.id} data-index={index} className="flex flex-col">
                <Card className={cn("relative flex h-full flex-col justify-between transition-all duration-500 rounded-md", isHighlighted ? "border-primary bg-card shadow-[0_0_50px_-12px_rgba(255,255,255,0.15)] lg:scale-105 z-10" : "border-border bg-card/30 hover:bg-card/50 hover:border-muted-foreground/50")}>
                  {isHighlighted && <div className="absolute -top-4 left-0 right-0 flex justify-center">
                      <Badge variant="default" className="rounded-md px-4 py-1 text-[10px] font-bold uppercase tracking-widest bg-primary text-primary-foreground shadow-md">
                        Most Popular
                      </Badge>
                    </div>}

                  <CardHeader className="pt-8">
                    <CardTitle className="font-default text-2xl font-bold text-foreground uppercase tracking-tight">
                      {tier.name}
                    </CardTitle>
                    <CardDescription className="mt-2 text-sm text-muted-foreground min-h-[40px] font-sans">
                      {tier.description}
                    </CardDescription>
                    <div className="mt-6 flex flex-col gap-y-1">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        Starting at
                      </span>
                      <span className="font-serif text-5xl font-medium tracking-tight text-foreground">
                        ${tier.price}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 pt-6">
                    <ul className="space-y-4">
                      {tierFeatures.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
                          <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-border">
                            <Check className="h-3 w-3 text-primary" />
                          </div>
                          <span className="text-sm text-muted-foreground font-sans">
                            {feature.text}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pb-8 pt-6">
                    <Button asChild variant={tier.buttonVariant} size="lg" className={cn("w-full rounded-md font-mono uppercase tracking-widest text-xs transition-colors duration-300", isHighlighted ? "bg-primary text-primary-foreground hover:bg-foreground hover:text-background" : "border-border hover:bg-primary hover:text-primary-foreground")}>
                      <Link to={tier.link}>{tier.buttonText}</Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}